import React from "react";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import GLAccountListTable from "../components/GLAccount/GLAccountListTable";
import CreateUpdateGLAccountDrawer, { GLAccountFormData, GLAccountToEdit } from "../components/GLAccount/CreateUpdateGLAccountDrawer";
import { createGLAccount, getAllGLAccounts, updateGLAccount, GLAccountCreateUpdateRequest, GLAccountResponse } from "../services/glAccountService";
import { getSystemSetup } from "../services/setupService";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const toRequestPayload = (formData: GLAccountFormData, status?: number | boolean | null): GLAccountCreateUpdateRequest => ({
    acc_code: formData.acc_code,
    acc_name: formData.acc_name,
    acc_branch_id: formData.acc_branch_id,
    status: typeof status === "boolean" ? (status ? 1 : 0) : status ?? 1,
});

const GLAccountManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [glAccountList, setGLAccountList] = React.useState<GLAccountResponse[]>([]);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedGLAccount, setSelectedGLAccount] = React.useState<GLAccountToEdit | undefined>(undefined);
    const [defaultBranchId, setDefaultBranchId] = React.useState<string>("");

    const fetchPageData = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching GL accounts...");

            const [accounts, setup] = await Promise.all([
                getAllGLAccounts(),
                getSystemSetup().catch(() => null),
            ]);

            setGLAccountList(accounts);
            setDefaultBranchId(setup?.setup_storeid ?? "");
        } catch (error: any) {
            toast.showError(`Error fetching GL accounts: ${error?.message || "Unknown error"}`);
            setGLAccountList([]);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    const handleSaveGLAccount = React.useCallback(async (accountData: GLAccountFormData, editingAccount?: GLAccountToEdit) => {
        const payload = toRequestPayload(accountData, editingAccount?.status ?? 1);

        if (editingAccount?.id) {
            await updateGLAccount(editingAccount.id, payload);
        } else {
            await createGLAccount(payload);
        }
    }, []);

    const handleEditGLAccount = React.useCallback((account: GLAccountResponse) => {
        setSelectedGLAccount({
            ...account,
            id: account.id ?? undefined,
        });
        setDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedGLAccount(undefined);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <GLAccountListTable
                    glAccountList={glAccountList}
                    onCreateGLAccount={canManageMasters ? () => setDrawerOpen(true) : undefined}
                    onEditGLAccount={handleEditGLAccount}
                />
            </Box>

            <CreateUpdateGLAccountDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                glAccountToEdit={selectedGLAccount}
                defaultBranchId={defaultBranchId}
                onSave={handleSaveGLAccount}
                onGLAccountSaved={fetchPageData}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default GLAccountManagement;
